import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ValidationResults = ({ validationResults }) => {
  const [selectedComparison, setSelectedComparison] = useState(null);

  const getValidationIcon = (status) => {
    switch (status) {
      case 'passed':
        return { name: 'CheckCircle', color: 'var(--color-success)' };
      case 'failed':
        return { name: 'XCircle', color: 'var(--color-error)' };
      case 'warning':
        return { name: 'AlertTriangle', color: 'var(--color-warning)' };
      default:
        return { name: 'Circle', color: 'var(--color-muted-foreground)' };
    }
  };

  return (
    <div className="bg-card rounded-lg p-6 border border-subtle">
      <div className="flex items-center gap-3 mb-6">
        <Icon name="CheckSquare" size={24} color="var(--color-primary)" />
        <h2 className="text-xl font-semibold text-foreground">Validation Results</h2>
      </div>
      <div className="space-y-4">
        {validationResults?.map((result, index) => {
          const statusIcon = getValidationIcon(result?.status);
          
          return (
            <div key={index} className="border border-subtle rounded-lg p-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 mt-1">
                  <Icon name={statusIcon?.name} size={20} color={statusIcon?.color} />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-foreground">{result?.testName}</h3>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      result?.status === 'passed' ? 'bg-success/20 text-success' :
                      result?.status === 'failed'? 'bg-error/20 text-error' : 'bg-warning/20 text-warning'
                    }`}>
                      {result?.status?.toUpperCase()}
                    </span>
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-3">
                    {result?.description}
                  </p>
                  
                  {result?.details && (
                    <div className="bg-muted/50 rounded p-3 mb-3">
                      <p className="text-sm text-foreground">{result?.details}</p>
                    </div>
                  )}
                  
                  {result?.screenshots && (
                    <div className="flex items-center gap-3">
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="Eye"
                        iconPosition="left"
                        onClick={() => setSelectedComparison(result)}
                      >
                        View Screenshots
                      </Button>
                      
                      {result?.status === 'failed' && result?.suggestion && (
                        <div className="flex items-center gap-2 text-sm text-warning">
                          <Icon name="Lightbulb" size={16} />
                          <span>{result?.suggestion}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/* Screenshot Comparison Modal */}
      {selectedComparison && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-6xl w-full max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between p-6 border-b border-subtle">
              <h3 className="text-lg font-semibold text-foreground">
                Screenshot Comparison: {selectedComparison?.testName}
              </h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSelectedComparison(null)}
              >
                <Icon name="X" size={20} />
              </Button>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Expected Screenshot */}
                <div>
                  <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
                    <Icon name="Target" size={16} color="var(--color-success)" />
                    Expected Result
                  </h4>
                  <div className="border border-subtle rounded-lg overflow-hidden">
                    <Image
                      src={selectedComparison?.screenshots?.expected}
                      alt="Expected result"
                      className="w-full h-auto"
                    />
                  </div>
                </div>
                
                {/* Actual Screenshot */}
                <div>
                  <h4 className="font-medium text-foreground mb-3 flex items-center gap-2">
                    <Icon name="Monitor" size={16} color="var(--color-primary)" />
                    Your Result
                  </h4>
                  <div className="border border-subtle rounded-lg overflow-hidden">
                    <Image
                      src={selectedComparison?.screenshots?.actual}
                      alt="Actual result"
                      className="w-full h-auto"
                    />
                  </div>
                </div>
              </div>
              
              {selectedComparison?.differences && (
                <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                  <h4 className="font-medium text-foreground mb-2 flex items-center gap-2">
                    <Icon name="GitCompare" size={16} />
                    Key Differences
                  </h4>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    {selectedComparison?.differences?.map((diff, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Icon name="ArrowRight" size={14} className="mt-0.5 flex-shrink-0" />
                        {diff}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ValidationResults;